import Component from '@glimmer/component';
import DocumentReviewCard from 'garaje/components/visitors/document-review-card';
import type CustomUserDocumentTemplateModel from 'garaje/models/custom-user-document-template';
import type VisitorDocumentModel from 'garaje/models/visitor-document';

interface DocumentRequirement {
  template: CustomUserDocumentTemplateModel | undefined;
  document: VisitorDocumentModel | undefined;
  isUploaded: boolean;
}

interface Signature {
  Args: {
    requiredTemplates?: CustomUserDocumentTemplateModel[];
    documents?: VisitorDocumentModel[];
    onStatusChange?: () => void;
  };
  Element: HTMLDivElement;
}

export default class DocumentReviewList extends Component<Signature> {
  get requirements(): DocumentRequirement[] {
    const docs = this.args.documents ?? [];
    const templates = this.args.requiredTemplates ?? [];
    const matchedDocIds = new Set<string>();

    // Match each required template to the first uploaded document (if any).
    // If multiple docs share the same template (e.g. re-upload after denial),
    // only the first is matched here — extras appear at the end of the list.
    const requirements: DocumentRequirement[] = templates.map((template) => {
      const doc = docs.find((d) => d.belongsTo('userDocumentTemplate').id() === template.id);
      if (doc) matchedDocIds.add(doc.id);
      return { template, document: doc, isUploaded: !!doc };
    });

    // Append extra uploaded docs that don't match any required template
    for (const doc of docs) {
      if (!matchedDocIds.has(doc.id)) {
        requirements.push({ template: undefined, document: doc, isUploaded: true });
      }
    }

    return requirements;
  }

  <template>
    <div data-test-document-review-list ...attributes>
      {{#each this.requirements as |req|}}
        <DocumentReviewCard
          @document={{req.document}}
          @template={{req.template}}
          @isUploaded={{req.isUploaded}}
          @onStatusChange={{@onStatusChange}}
        />
      {{/each}}

      {{#unless this.requirements.length}}
        <p class="text-carbon-50 text-center py-8" data-test-no-documents>
          No documents required for this visitor type.
        </p>
      {{/unless}}
    </div>
  </template>
}
